import { FileUploaded } from "@archetypes/generic";
import { FILE_UPLOAD_URL } from "@config/index";
import {
  FileExcelIcon,
  FileIcon,
  FileImageIcon,
  FilePDFIcon,
  FileDocIcon,
} from "@components/icons";
import { FileTypeEnum, FilterFileTypesEnum } from "@archetypes/form-element";
import { Button } from "@components/button";
import { CustomTooltip } from "@components/tool-tip";

export const fileUpload = async (filePath: string, file: Blob, fileName: string) => {
  const formData = new FormData();
  formData.append("filePath", filePath);
  formData.append("file", file, fileName);

  try {
    const rawResult = await fetch(FILE_UPLOAD_URL, {
      method: "POST",
      body: formData,
    });

    const result = (await rawResult.json()) as { data: FileUploaded[] };
    return result.data;
  } catch (err) {
    throw new Error("Error uploading file");
  }
};

export const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case FileTypeEnum.PDF:
      return <FilePDFIcon size="2.5rem" />;
    case FileTypeEnum.DOCX:
      return <FileDocIcon size="2.5rem" />;
    case FileTypeEnum.IMAGE:
      return <FileImageIcon size="2.5rem" />;
    case FileTypeEnum.EXCEL:
      return <FileExcelIcon size="2.5rem" />;
    case FileTypeEnum.XLSX:
      return <FileExcelIcon size="2.5rem" />;
    default:
      return <FileIcon size="2.5rem" />;
  }
};

export const getFileTypeLabel = (fileType: string) => {
  switch (fileType) {
    case FilterFileTypesEnum.PDF:
      return "Pdf";
    case FilterFileTypesEnum.DOCUMENT:
      return "Document";
    case FilterFileTypesEnum.IMAGE:
      return "Image";
    case FilterFileTypesEnum.SPREADSHEET:
      return "Spreadsheet";
    case FilterFileTypesEnum.PRESENTATION:
      return "Presentation";
    default:
      return "File";
  }
};

export const getFileNameFromPath = (path: string) => {
  return path.slice(path.lastIndexOf("/") + 1);
};

export const getFileSizeAsString = (size: number) => {
  if (size === 1) {
    return 1 + " Byte";
  }
  if (size < 1024) {
    return size + " Bytes";
  }
  if (size === 1024) {
    return 1 + " KB";
  }
  if (size > 1024 && size < 1024 * 1024) {
    return size / 1024 + " KB";
  }
  if (size === 1024) {
    return 1 + " MB";
  }
  if (size > 1024 * 1024) {
    return size / (1024 * 1024) + " MB";
  }
};

export const checkFaviconResolution = (file: File) => {
  return new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      if ((width === 16 || width === 32) && (height === 16 || height === 32)) {
        resolve();
      } else {
        reject(new Error("Favicon resolution must be either 16x16 or 32x32"));
      }
    };
    img.onerror = () => reject(new Error("Failed to load image for resolution check."));
    img.src = URL.createObjectURL(file);
  });
};

export const getFileNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "image.jpg";
  } catch {
    return "image.jpg";
  }
};

export const getFileTypeFromUrl = (url: string): FileTypeEnum => {
  const extension = url.split(".").pop()?.split("?")[0].toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "webp":
    case "avif":
      return FileTypeEnum.IMAGE;
    case "pdf":
      return FileTypeEnum.PDF;
    case "docx":
      return FileTypeEnum.DOCX;
    case "xls":
    case "xlsx":
      return FileTypeEnum.DOCX;
    default:
      return FileTypeEnum.XLSX;
  }
};

export const iconMap: Record<string, JSX.Element> = {
  pdf: <FilePDFIcon size="1.9rem" />,
  docx: <FileDocIcon size="1.9rem" />,
  doc: <FileDocIcon size="1.9rem" />,
  pptx: <FileDocIcon size="1.9rem" />,
  ppt: <FileDocIcon size="1.9rem" />,
};
export const imageExtensions = ["jpeg", "jpg", "png", "svg"];

export const renderImagePreview = ({
  path,
  onImageClick,
  isMobile,
}: {
  path: string;
  onImageClick?: (path: string) => void;
  isMobile?: boolean;
}) => {
  const image = (
    <img
      src={path}
      alt="response"
      loading="eager"
      decoding="async"
      className="h-full w-full object-cover"
    />
  );
  return (
    <Button onClick={() => onImageClick?.(path)} className="mr-10 p-0 w-32 h-32 m-5 round-1">
      {isMobile ? (
        image
      ) : (
        <CustomTooltip
          wrapperClass="h-full w-full"
          content={getFileNameFromPath(path)}
          direction="right"
        >
          {image}
        </CustomTooltip>
      )}
    </Button>
  );
};
